import { Button, InfoBadge, InfoBar, InfoPage } from "@shapr3d/frontend-lib";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { useHandleLoginRedirect } from "../../containers/AppAuthContainer";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useResendVerificationEmail } from "../../hooks/useAuth";
import { useRequestProjectAccess } from "../../hooks/useProjectSharing";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";

import style from "./RequestAccessPage.module.css";

export default function RequestAccessPage({ isPendingRequest }: { isPendingRequest?: boolean }) {
  const { data: accountData } = useAccount();
  const { projectId } = useParams();
  const tracker = useTracker();
  const { t, m } = useTranslation();

  const onLoginRedirect = useHandleLoginRedirect();
  const handleLoginRedirect = () => {
    tracker.trackProjectPageAccessSwitchUser({
      projectId: projectId || "",
    });
    onLoginRedirect();
  };
  const {
    mutate: mutateResendVerificationEmail,
    isPending: isResendVerificationEmailPending,
    isSuccess: isResendVerificationEmailSuccessful,
  } = useResendVerificationEmail();

  const queryClient = useQueryClient();

  const {
    mutateAsync: requestProjectAccessMutate,
    isPending: isRequestAccessPending,
    isError: isRequestAccessError,
    isSuccess: isRequestAccessSuccessful,
  } = useRequestProjectAccess({});

  const handleRequestAccess = () => {
    tracker.trackProjectPageAccessRequested({
      projectId: projectId || "",
    });
    requestProjectAccessMutate();
    queryClient.invalidateQueries({ queryKey: ["project"] });
  };

  const { goToProfile } = useNavigation();

  if (isPendingRequest || isRequestAccessSuccessful) {
    return (
      <InfoPage
        variant="success"
        title={t(m.ErrorMessage.AccessRequested)}
        description={
          <div className={style.message}>{t(m.ErrorMessage.AccessRequestedDescription)}</div>
        }
        primaryCTA={
          <Button
            variant="grayscale"
            onClick={() => {
              tracker.trackGoToDashboard({
                location: "request_access_page",
                destination: "shared_with_me",
              });
              goToProfile();
            }}
          >
            {t(m.ErrorMessage.GoToDashboard)}
          </Button>
        }
      />
    );
  }

  return (
    <InfoPage
      variant="permission"
      title={
        !accountData?.emailVerified
          ? t(m.ErrorMessage.EmailNotVerified)
          : t(m.ErrorMessage.RequestAccessToProject)
      }
      description={
        <>
          <div className={style.message}>
            {!accountData?.emailVerified
              ? t(m.ErrorMessage.VerifyEmailToAccessProject)
              : t(m.ErrorMessage.AccessRequestedDescription)}
          </div>
          <InfoBadge
            superTitle={t(m.ErrorMessage.YoureCurrentlyLoggedInAs)}
            title={accountData?.name}
            description={accountData?.email}
            cta={
              <Button variant="grayscale" onClick={handleLoginRedirect}>
                {t(m.ErrorMessage.SwitchUserProfile)}
              </Button>
            }
          />
        </>
      }
      primaryCTALabel={
        (isResendVerificationEmailSuccessful && (
          <InfoBar
            variant="success"
            title={t(m.ErrorMessage.VerificationEmailSent)}
            text={t(m.ErrorMessage.VerificationEmailSentDescription)}
            className={style.successInfoBar}
          />
        )) ||
        (isRequestAccessError && (
          <InfoBar
            variant="critical"
            title={t(m.ErrorMessage.RequestProcessingFailed)}
            text={t(m.ErrorMessage.RequestProcessingFailedDescription)}
          />
        ))
      }
      primaryCTA={
        <>
          {!accountData?.emailVerified && !isResendVerificationEmailSuccessful ? (
            <Button
              variant="primary"
              disabled={isResendVerificationEmailPending}
              onClick={() => {
                tracker.trackRequestVerificationEmailClicked({ location: "request_access" });
                mutateResendVerificationEmail();
              }}
            >
              {t(m.ErrorMessage.VerifyEmailToContinue)}
            </Button>
          ) : (
            !isResendVerificationEmailSuccessful && (
              <Button
                variant="primary"
                onClick={handleRequestAccess}
                disabled={isRequestAccessPending}
              >
                {t(m.ErrorMessage.RequestAccess)}
              </Button>
            )
          )}
        </>
      }
    />
  );
}
